import type { ReactNode } from 'react';
import React, { use, useState } from 'react';

import { Button, Switch } from 'antd';
import type { MenuProps } from 'antd';
import { Popover } from 'antd-mobile';
import classNames from 'classnames';

import alertCaller from '@core/app/actions/alert-caller';
import { modelsWithModules } from '@core/app/actions/beambox/constant';
import presprayArea from '@core/app/actions/canvas/prespray-area';
import alertConstants from '@core/app/constants/alert-constants';
import colorConstants from '@core/app/constants/color-constants';
import type { PrintingColors } from '@core/app/constants/color-constants';
import { printingModules } from '@core/app/constants/layer-module/layer-modules';
import LayerPanelIcons from '@core/app/icons/layer-panel/LayerPanelIcons';
import ObjectPanelIcons from '@core/app/icons/object-panel/ObjectPanelIcons';
import { useLayerStore } from '@core/app/stores/layer/layerStore';
import { useIsMobile } from '@core/app/stores/screenStore';
import undoManager from '@core/app/svgedit/history/undoManager';
import layerManager from '@core/app/svgedit/layer/layerManager';
import selectionManager from '@core/app/svgedit/selection';
import colorPickerStyles from '@core/app/widgets/ColorPicker.module.scss';
import ContextMenu from '@core/app/widgets/ContextMenu';
import updateLayerColor from '@core/helpers/color/updateLayerColor';
import useWorkarea from '@core/helpers/hooks/useWorkarea';
import { deleteLayers } from '@core/helpers/layer/deleteLayer';
import splitFullColorLayer from '@core/helpers/layer/full-color/splitFullColorLayer';
import toggleFullColorLayer from '@core/helpers/layer/full-color/toggleFullColorLayer';
import { getData } from '@core/helpers/layer/layer-config-helper';
import {
  cloneLayers,
  getLayerName,
  getLayerPosition,
  mergeLayers,
  setLayersLock,
} from '@core/helpers/layer/layer-helper';
import useI18n from '@core/helpers/useI18n';

import { ObjectPanelContext } from '../contexts/ObjectPanelContext';
import ObjectPanelItem from '../ObjectPanelItem';

import styles from './LayerContextMenu.module.scss';

interface Props {
  children?: ReactNode;
  renameLayer: () => void;
  selectOnlyLayer: (name: string) => void;
}

const LayerContextMenu = ({ children, renameLayer, selectOnlyLayer }: Props): React.JSX.Element => {
  const lang = useI18n();
  const LANG = lang.beambox.right_panel.layer_panel;
  const LANG2 = lang.alert;
  const workarea = useWorkarea();
  const selectedLayers = useLayerStore((state) => state.selectedLayers);
  const isMobile = useIsMobile();
  const { activeKey, updateActiveKey } = use(ObjectPanelContext);
  const [color, setColor] = useState(colorConstants.printingLayerColor[0]);
  const layerElem = layerManager.getLayerElementByName(selectedLayers[0])!;
  const isLocked = layerElem?.getAttribute('data-lock') === 'true';
  const handleContextMenu = (e: React.MouseEvent) => {
    const target = e.target as Element;
    const layerItem = target?.closest('.layer-item');
    const layerName = layerItem?.getAttribute('data-layer');

    if (layerName && !selectedLayers.includes(layerName)) {
      selectOnlyLayer(layerName);
    }
  };

  const handleRename = () => {
    selectOnlyLayer(selectedLayers[0]);
    renameLayer();
  };

  const handleCloneLayers = () => {
    const newLayers = cloneLayers(selectedLayers);

    layerManager.setSelectedLayers(newLayers);
  };

  const handleDeleteLayers = () => {
    deleteLayers(selectedLayers);
    presprayArea.togglePresprayArea();
  };

  const toggleLayerLocked = () => {
    selectionManager.clearSelection();
    setLayersLock(selectedLayers, !isLocked);
    layerManager.resync();
  };

  const handleMergeDown = async () => {
    const layer = selectedLayers[0];
    const layerPosition = getLayerPosition(layer);

    if (layerPosition === 0) {
      return;
    }

    const baseLayerName = layerManager.getLayerName(layerPosition - 1)!;
    const merged = await mergeLayers([layer], baseLayerName);

    if (merged) {
      selectOnlyLayer(baseLayerName);
    }
  };

  const handleMergeAll = async () => {
    const allLayerNames = layerManager.getAllLayerNames();
    const baseLayerName = await mergeLayers(allLayerNames);

    if (!baseLayerName) {
      return;
    }

    const elem = layerManager.getLayerElementByName(baseLayerName!);

    updateLayerColor(elem as SVGGElement);
    selectOnlyLayer(baseLayerName);
  };

  const handleMergeSelected = async () => {
    const currentLayerName = layerManager.getCurrentLayerName()!;
    const baseLayer = await mergeLayers(selectedLayers, currentLayerName);

    if (!baseLayer) {
      return;
    }

    const elem = layerManager.getLayerElementByName(baseLayer!);

    updateLayerColor(elem as SVGGElement);
    layerManager.setSelectedLayers([baseLayer]);
  };

  const isSelectingPrinterLayer =
    selectedLayers.length === 1 &&
    layerElem &&
    modelsWithModules.has(workarea) &&
    printingModules.has(getData(layerElem, 'module')!);
  const isFullColor = getData(layerElem, 'fullcolor');
  const isSplitLayer = getData(layerElem, 'split');

  const handleSplitColor = async () => {
    if (!isSelectingPrinterLayer || isSplitLayer) {
      return;
    }

    const res = await new Promise<boolean>((resolve) => {
      alertCaller.popUp({
        buttonType: alertConstants.CONFIRM_CANCEL,
        caption: LANG.notification.splitColorTitle,
        id: 'split-color',
        message: LANG.notification.splitColorMsg,
        messageIcon: 'notice',
        onCancel: () => resolve(false),
        onConfirm: () => resolve(true),
      });
    });

    if (!res) {
      return;
    }

    const layer = selectedLayers[0];

    const splitRes = await splitFullColorLayer(layer);

    layerManager.setSelectedLayers(splitRes?.newLayers.map(getLayerName) ?? []);
  };

  const handleLayerFullColor = (newColor?: string) => {
    selectionManager.clearSelection();

    if (!isSelectingPrinterLayer) {
      return;
    }

    if (
      isFullColor &&
      (newColor || !colorConstants.printingLayerColor.includes(layerElem.getAttribute('data-color') as PrintingColors))
    ) {
      layerElem.setAttribute('data-color', newColor || colorConstants.printingLayerColor[0]);
    }

    const cmd = toggleFullColorLayer(layerElem);

    if (cmd && !cmd.isEmpty()) {
      undoManager.addCommandToHistory(cmd);
    }

    layerManager.resync();
  };

  const isMultiSelecting = selectedLayers.length > 1;
  const isSelectingLast = selectedLayers.length === 1 && layerManager.getLayerName(0) === selectedLayers[0];

  return isMobile ? (
    <>
      {children}
      <div className={styles['item-group']}>
        <ObjectPanelItem.Divider />
        {isSelectingPrinterLayer && (
          <ObjectPanelItem.Item
            content={<LayerPanelIcons.Expand />}
            disabled={isMultiSelecting || !isFullColor}
            id="split_color"
            label={LANG.layers.splitFullColor}
            onClick={handleSplitColor}
          />
        )}
        {isSelectingPrinterLayer && (
          <Popover
            content={
              <>
                <div className={colorPickerStyles.preset}>
                  {colorConstants.printingLayerColor.map((preset) => (
                    <div
                      className={classNames(
                        colorPickerStyles['preset-block'],
                        colorPickerStyles.color,
                        colorPickerStyles.printing,
                        { [colorPickerStyles.checked]: preset === color },
                      )}
                      key={preset}
                      onClick={() => setColor(preset)}
                    >
                      <div className={colorPickerStyles.inner} style={{ backgroundColor: preset }} />
                    </div>
                  ))}
                </div>
                <div className={colorPickerStyles.footer}>
                  <Button
                    className={colorPickerStyles.btn}
                    onClick={() => {
                      updateActiveKey(null);
                      handleLayerFullColor(color);
                      setColor(colorConstants.printingLayerColor[0]);
                    }}
                    type="primary"
                  >
                    {LANG2.ok}
                  </Button>
                  <Button
                    className={colorPickerStyles.btn}
                    onClick={() => {
                      updateActiveKey(null);
                      setColor(colorConstants.printingLayerColor[0]);
                    }}
                    type="default"
                  >
                    {LANG2.cancel}
                  </Button>
                </div>
              </>
            }
            visible={activeKey === 'toggle_fullcolor_layer' && isFullColor}
          >
            <ObjectPanelItem.Item
              autoClose={false}
              content={<Switch checked={isFullColor} />}
              disabled={isMultiSelecting || isSplitLayer}
              id="toggle_fullcolor_layer"
              label={LANG.layers.fullColor}
              onClick={() => {
                if (!isFullColor && !isSplitLayer) {
                  handleLayerFullColor();
                  updateActiveKey(null);
                }
              }}
            />
          </Popover>
        )}
        <ObjectPanelItem.Item
          content={<ObjectPanelIcons.Trash />}
          id="deletelayer"
          label={LANG.layers.del}
          onClick={handleDeleteLayers}
        />
        <ObjectPanelItem.Item
          content={<LayerPanelIcons.Merge />}
          disabled={isMultiSelecting || isSelectingLast}
          id="merge_down_layer"
          label={LANG.layers.merge_down}
          onClick={handleMergeDown}
        />
        <ObjectPanelItem.Item
          content={isLocked ? <LayerPanelIcons.Unlock /> : <LayerPanelIcons.Lock />}
          id="locklayer"
          label={isLocked ? LANG.layers.unlock : LANG.layers.lock}
          onClick={toggleLayerLocked}
        />
        <ObjectPanelItem.Item
          content={<ObjectPanelIcons.Duplicate />}
          id="dupelayer"
          label={LANG.layers.dupe}
          onClick={handleCloneLayers}
        />
        <ObjectPanelItem.Item
          content={<LayerPanelIcons.Rename />}
          disabled={isMultiSelecting}
          id="renameLayer"
          label={LANG.layers.rename}
          onClick={handleRename}
        />
      </div>
    </>
  ) : (
    <ContextMenu
      items={
        [
          { disabled: isMultiSelecting, key: 'renameLayer', label: LANG.layers.rename, onClick: handleRename },
          { key: 'dupelayer', label: LANG.layers.dupe, onClick: handleCloneLayers },
          {
            key: 'locklayer',
            label: isLocked ? LANG.layers.unlock : LANG.layers.lock,
            onClick: toggleLayerLocked,
          },
          { key: 'deletelayer', label: LANG.layers.del, onClick: handleDeleteLayers },
          {
            disabled: isMultiSelecting || isSelectingLast,
            key: 'merge_down_layer',
            label: LANG.layers.merge_down,
            onClick: handleMergeDown,
          },
          {
            disabled: isMultiSelecting,
            key: 'merge_all_layer',
            label: LANG.layers.merge_all,
            onClick: handleMergeAll,
          },
          {
            disabled: !isMultiSelecting,
            key: 'merge_selected_layer',
            label: LANG.layers.merge_selected,
            onClick: handleMergeSelected,
          },
          ...(isSelectingPrinterLayer
            ? [
                {
                  disabled: isMultiSelecting || isSplitLayer,
                  key: 'toggle_fullcolor_layer',
                  label: isFullColor ? LANG.layers.switchToSingleColor : LANG.layers.switchToFullColor,
                  onClick: () => handleLayerFullColor(),
                },
                {
                  disabled: isMultiSelecting || !isFullColor,
                  key: 'split_color',
                  label: LANG.layers.splitFullColor,
                  onClick: handleSplitColor,
                },
              ]
            : []),
        ] satisfies MenuProps['items']
      }
    >
      <div onContextMenu={handleContextMenu}>{children}</div>
    </ContextMenu>
  );
};

export default LayerContextMenu;
